import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import IconPicker from '../../components/admin/IconPicker';
import { operationApi } from '../../services/api';
import { Edit, Trash2 } from 'lucide-react';

export default function OperationCategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 15 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', description: '', icon: '', order: 0, is_active: true });

  const fetchCategories = async (page = 1) => {
    setLoading(true);
    try {
      const res = await operationApi.inventoryCategories.list({ per_page: 15, page });
      const d = res.data.data;
      setCategories(d?.data || d || []);
      setPagination({ current_page: d?.current_page || 1, last_page: d?.last_page || 1, total: d?.total || 0, per_page: d?.per_page || 15 });
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const pagesArr = []; for (let i = Math.max(1, pagination.current_page - 2); i <= Math.min(pagination.last_page, pagination.current_page + 2); i++) { pagesArr.push(i); }

  useEffect(() => { fetchCategories(); }, []);

  const resetForm = () => setForm({ name: '', slug: '', description: '', icon: '', order: 0, is_active: true });

  const openNew = () => { setEditing(null); resetForm(); setShowForm(true); };

  const handleEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name || '', slug: cat.slug || '', description: cat.description || '', icon: cat.icon || '', order: cat.order || 0, is_active: cat.is_active ?? true });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await operationApi.inventoryCategories.update(editing.id, form);
      } else {
        await operationApi.inventoryCategories.create(form);
      }
      setShowForm(false); setEditing(null); resetForm();
      fetchCategories(pagination.current_page);
    } catch (err) { alert('Gagal: ' + (err.response?.data?.message || '')); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus kategori ini? Semua item dalam kategori ini akan kehilangan kategorinya.')) return;
    try { await operationApi.inventoryCategories.delete(id); fetchCategories(pagination.current_page); } catch (err) { alert('Gagal menghapus'); }
  };

  return (
    <AdminLayout active="Kategori Inventaris">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-lg font-semibold text-gray-800 mb-1">Kategori Inventaris</h1>
          <button onClick={openNew} className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark text-sm">+ Tambah Kategori</button>
        </div>

        {loading ? <div className="text-center py-10 text-gray-500">Loading...</div> : (<>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategori</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Icon</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deskripsi</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th><th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aksi</th></tr></thead>
              <tbody className="divide-y divide-gray-200">
                {categories.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3"><p className="text-sm font-medium text-gray-900">{c.name}</p><p className="text-xs text-gray-400">{c.slug}</p></td>
                    <td className="px-4 py-3 text-xl">{c.icon || '-'}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-xs truncate">{c.description || '-'}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand/10 text-navy text-xs font-semibold">{c.items_count ?? 0}</span></td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c.order}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 text-xs rounded-full font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{c.is_active ? 'Aktif' : 'Nonaktif'}</span></td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <button onClick={() => handleEdit(c)} className="text-blue-600 hover:text-blue-800 text-xs"><Edit size={14} /></button>
                      <button onClick={() => handleDelete(c.id)} className="text-red-600 hover:text-red-800 text-xs"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
                {categories.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Belum ada kategori inventaris</td></tr>}
              </tbody>
            </table>
          </div>
          {pagination.last_page > 1 && (
            <div className="bg-white rounded-lg shadow overflow-hidden border-t border-gray-100">
              <div className="flex items-center justify-between px-4 py-3">
                <span className="text-sm text-gray-500">Total {pagination.total} data · Halaman {pagination.current_page} dari {pagination.last_page}</span>
                <div className="flex items-center gap-1">
                  <button disabled={pagination.current_page === 1} onClick={() => fetchCategories(pagination.current_page - 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-50">« Prev</button>
                  {pagesArr.map(p => (<button key={p} onClick={() => fetchCategories(p)} className={`px-3 py-1 text-sm rounded ${p === pagination.current_page ? 'bg-brand text-white' : 'border border-gray-300 hover:bg-gray-50'}`}>{p}</button>))}
                  <button disabled={pagination.current_page === pagination.last_page} onClick={() => fetchCategories(pagination.current_page + 1)} className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-50">Next »</button>
                </div>
              </div>
            </div>
          )}
        </>)}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b"><h2 className="text-lg font-bold text-gray-800">{editing ? 'Edit Kategori' : 'Tambah Kategori'}</h2><button onClick={() => { setShowForm(false); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-xl">✕</button></div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Nama *</label><input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Slug *</label><input required value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" /></div>
               <div><IconPicker label="Icon" value={form.icon} onChange={v => setForm({ ...form, icon: v })} placeholder="Pilih icon..." /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Deskripsi</label><textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Urutan</label><input type="number" min={0} value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" /></div>
                <div><label className="block text-xs font-medium text-gray-600 mb-1">Status</label><select value={form.is_active ? '1' : '0'} onChange={e => setForm({ ...form, is_active: e.target.value === '1' })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"><option value="1">Aktif</option><option value="0">Nonaktif</option></select></div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t"><button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Batal</button><button type="submit" disabled={saving} className="px-4 py-2 bg-brand text-white rounded-lg text-sm hover:bg-brand-dark disabled:opacity-50">{saving ? 'Menyimpan...' : 'Simpan'}</button></div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}